import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, 
  Avatar, 
  Typography, 
  CircularProgress 
} from '@mui/material';
import { auth } from '../../auth/appAuth';
import { updateProfile } from '../../auth/staffAuthApi';
import { upsertEmployee } from '../../api/companyRecords';
import { patchCompany } from '../../api/company/company';
import { patchCurrentUser } from '../../api/me';
import { deleteStoredFile, objectFileName, objectPathFromUrl, uploadFile } from '../../api/files';

const AvatarUpload = ({ user, company, setNotification, updateUser, updateCompany, ownerId }) => {
  const [uploading, setUploading] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  const avatarSrc = useMemo(() => {
    if (user) {
      return user.photoURL || auth.currentUser?.photoURL || null;
    }
    return company?.logoUrl || null;
  }, [user, company?.logoUrl, user?.photoURL]);

  const showInitials = Boolean(user && !avatarSrc) || imageLoadFailed;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [avatarSrc]);

  const deleteOldPhoto = async (photoURL) => {
    const path = objectPathFromUrl(photoURL);
    if (!path) return;
    try {
      await deleteStoredFile(path);
    } catch (error) {
      console.error('Error deleting old photo:', error);
    }
  };

  const handlePhotoSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setNotification({
        open: true,
        message: 'Please select an image file',
        severity: 'error'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNotification({
        open: true,
        message: 'Image must be less than 5MB',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    setImageLoadFailed(false);

    try {
      let downloadURL;
      
      if (user) {
        const userId = user.uid || auth.currentUser?.uid;
        const stored = await uploadFile(file, `profile-photos/${userId}/${objectFileName(file)}`);
        downloadURL = stored.url;

        if (user.photoURL) {
          await deleteOldPhoto(user.photoURL);
        }

        await patchCurrentUser({
          photoURL: downloadURL,
        });

        // Update local state immediately so the avatar shows before auth rehydration runs.
        if (updateUser) {
          updateUser((prev) => ({ ...(prev || user), photoURL: downloadURL }));
        }

        // Sync the signed-in profile in the background.
        const currentUser = auth.currentUser;
        if (currentUser) {
          updateProfile(currentUser, { photoURL: downloadURL })
            .then(() => currentUser.getIdToken(true))
            .catch((err) => console.warn('Auth profile photo sync:', err.message));
        }

        // Update employee records in Postgres (non-blocking)
        const employeeCompanyId = user.companyId || company?.id;
        if (employeeCompanyId && user.uid) {
          upsertEmployee({
            companyId: employeeCompanyId,
            id: user.uid,
            employee: { photoURL: downloadURL, updatedAt: new Date().toISOString() },
          }).catch((err) => console.warn('Employee photo update:', err.message));
        }

        setNotification({
          open: true,
          message: 'Profile photo updated successfully',
          severity: 'success'
        });
      } else if (company) {
        // Handle company logo upload first
        const stored = await uploadFile(file, `companies/${company.id}/logo/${objectFileName(file)}`);
        downloadURL = stored.url;

        // Only try to delete the old logo after successful upload
        if (company.logoUrl) {
          try {
            // Use the same deletion logic as user photos
            await deleteOldPhoto(company.logoUrl);
          } catch (error) {
            console.error('Error deleting old company logo:', error);
            // Continue with the upload even if deletion fails
          }
        }

        const updatedCompany = await patchCompany({
          companyId: company.id,
          company: { logoUrl: downloadURL },
        });

        if (updateCompany) {
          updateCompany({ ...updatedCompany, logoUrl: downloadURL });
        }

        setNotification({
          open: true,
          message: 'Company logo updated successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update image',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      if (event && event.target) event.target.value = '';
    }
  };

  const getInitials = () => {
    if (user && user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    return '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="avatar-upload"
        type="file"
        onChange={handlePhotoSelect}
      />
      <label htmlFor="avatar-upload">
        <Box 
          sx={{ 
            position: 'relative',
            cursor: 'pointer',
            '&:hover': {
              '&::after': {
                content: '"Change"',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontSize: '14px',
                fontWeight: 'bold'
              }
            }
          }}
        >
          <Avatar
            src={imageLoadFailed ? undefined : (avatarSrc || undefined)}
            imgProps={{
              onError: () => setImageLoadFailed(true),
              onLoad: () => setImageLoadFailed(false),
            }}
            sx={{ 
              width: 120, 
              height: 120, 
              mb: 1,
              border: '2px solid #e0e0e0'
            }}
          >
            {showInitials && getInitials()}
          </Avatar>
          {uploading && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '50%'
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>
      </label>
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        Click avatar to update {user ? 'profile photo' : 'company logo'}
      </Typography>
    </Box>
  );
};

export default AvatarUpload;